/**
 * Mobile chart readability sizing profile.
 *
 * DXChart splits typography across two customization surfaces: its React UI
 * reads design-system CSS variables (handled in `src/index.css`), while its
 * canvas-rendered labels read numeric `fontSize` fields from
 * `initialChartConfig`. Canvas fonts therefore cannot be adjusted with CSS and
 * are applied here instead.
 *
 * The breakpoint matches DXChart's own toolbar breakpoint so the vendor layout
 * and the UTC sizing profile switch together.
 */

export const MOBILE_CHART_BREAKPOINT_PX = 756

export type ChartSizingProfile = 'mobile' | 'desktop'

export function resolveChartSizingProfile(viewportWidth: number): ChartSizingProfile {
  return viewportWidth <= MOBILE_CHART_BREAKPOINT_PX ? 'mobile' : 'desktop'
}

/**
 * The installed DXChart default for both axis label fonts.
 *
 * Audited against the vendor defaults: `waterMark`, `highLow`, `highlights`,
 * and `navigationMap.timeLabels` all default to `visible: false` and are not
 * enabled by the tournament configuration, so they are deliberately omitted.
 */
const VENDOR_AXIS_FONT_SIZE_PX = 12

/**
 * Resolve a mobile font size as "one pixel larger than the size actually in
 * effect". A caller-provided size is the effective size, so it is incremented
 * rather than replaced — a caller asking for 15px gets 16px on mobile, not the
 * vendor-derived 13px.
 */
function enlargeByOnePixel(callerFontSize: unknown): number {
  return typeof callerFontSize === 'number' && Number.isFinite(callerFontSize)
    ? callerFontSize + 1
    : VENDOR_AXIS_FONT_SIZE_PX + 1
}

/**
 * Merge the mobile canvas font sizes into an existing chart config without
 * discarding any caller-provided settings (notably the tournament's Y-axis
 * `labels` settings, and any explicit caller font size, which is incremented
 * rather than overwritten). Returns the config unchanged for the desktop
 * profile.
 */
export function withMobileCanvasTypography<T extends Record<string, any> | undefined>(
  initialChartConfig: T,
  profile: ChartSizingProfile,
): T | Record<string, any> {
  if (profile !== 'mobile') {
    return initialChartConfig
  }

  const config = initialChartConfig ?? {}
  const components = (config as Record<string, any>).components ?? {}

  return {
    ...config,
    components: {
      ...components,
      xAxis: {
        ...(components.xAxis ?? {}),
        fontSize: enlargeByOnePixel(components.xAxis?.fontSize),
      },
      yAxis: {
        ...(components.yAxis ?? {}),
        fontSize: enlargeByOnePixel(components.yAxis?.fontSize),
      },
    },
  }
}
